import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { fetchDashboard } from '../../store/slices/shipmentTrackerSlice';
import ActiveShipmentsTable from '../../components/shipment-tracker/ActiveShipmentsTable';
import ShipmentStatusBadge from '../../components/shipment-tracker/ShipmentStatusBadge';
import type { ShipmentRecentActivityView } from '../../api/generated/models/ShipmentRecentActivityView';

function formatDashboardEvent(
  event: Record<string, string> | undefined,
  t: (k: string, o?: Record<string, string>) => string
): string {
  if (!event) return '-';
  const { type, from, to, body, productName, fileName, label } = event;
  switch (type) {
    case 'status_changed':
      return t('activity_status_changed', {
        from: from ? t(`shipment_status_${from}`) : t('none'),
        to: to ? t(`shipment_status_${to}`) : '-',
      });
    case 'item_added':
      return t('activity_item_added', { name: productName ?? '' });
    case 'item_deleted':
      return t('activity_item_deleted', { name: productName ?? '' });
    case 'item_edited':
      return t('activity_item_edited', { name: productName ?? '' });
    case 'comment_posted':
      return body ? `${t('activity_comment_posted')}: ${body}` : t('activity_comment_posted');
    case 'shipment_created':
      return t('activity_shipment_created');
    case 'edited':
      return t('activity_shipment_edited');
    case 'reconciliation_updated':
      return t('activity_reconciliation_updated');
    case 'allocation_method_changed':
      return t('activity_allocation_method_changed');
    case 'cost_component_added':
      return t('activity_cost_component_added', { label: label ?? '' });
    case 'cost_component_edited':
      return t('activity_cost_component_edited', { label: label ?? '' });
    case 'cost_component_deleted':
      return t('activity_cost_component_deleted', { label: label ?? '' });
    case 'document_attached':
      return t('activity_document_attached', { name: fileName ?? '' });
    case 'document_deleted':
      return t('activity_document_deleted', { name: fileName ?? '' });
    case 'archived':
      return t('activity_shipment_archived');
    case 'restored':
      return t('activity_shipment_restored');
    default:
      return type ?? '-';
  }
}

function formatActivityTime(iso: string) {
  return new Date(iso).toLocaleDateString();
}

const ShipmentTrackerDashboardPage: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { recentActivity, loading } = useAppSelector(
    (state) => state.shipmentTracker.dashboard
  );

  useEffect(() => {
    dispatch(fetchDashboard({}));
  }, [dispatch]);

  return (
    <div className="page shipment-dashboard-page">
      <h1 className="page-title">{t('shipment_dashboard_title')}</h1>

      {/* Active shipments */}
      <div className="dashboard-section">
        <ActiveShipmentsTable showViewAllLink showCsvButton={false} limitOverride={10} />
      </div>

      {/* Recent activity */}
      <div className="dashboard-section">
        <div className="detail-card">
          <h2 className="detail-card__title">{t('dashboard_recent_activity')}</h2>
          {loading ? (
            <p>{t('loading')}</p>
          ) : recentActivity.length === 0 ? (
            <p className="detail-card__empty">{t('dashboard_no_activity')}</p>
          ) : (
            <div className="shipment-activity-table-scroll">
              <table className="shipment-activity-table">
                <thead>
                  <tr>
                    <th>{t('dashboard_activity_col_time')}</th>
                    <th>{t('dashboard_activity_col_ref')}</th>
                    <th>{t('dashboard_activity_col_action')}</th>
                    <th>{t('dashboard_activity_col_user')}</th>
                    <th>{t('dashboard_activity_col_status')}</th>
                  </tr>
                </thead>
                <tbody>
                  {recentActivity.map((item: ShipmentRecentActivityView, idx) => (
                    <tr key={idx}>
                      <td>{formatActivityTime(item.time)}</td>
                      <td>{item.shipmentReference ?? '-'}</td>
                      <td>{formatDashboardEvent(item.event, t)}</td>
                      <td>{item.userName ?? '-'}</td>
                      <td>
                        {item.statusBadge ? (
                          <ShipmentStatusBadge status={item.statusBadge} />
                        ) : (
                          '-'
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Exceptions & alerts */}
      <div className="dashboard-section">
        <div className="detail-card">
          <h2 className="detail-card__title">{t('dashboard_alerts')}</h2>
          <p className="detail-card__empty">{t('dashboard_no_alerts')}</p>
        </div>
      </div>
    </div>
  );
};

export default ShipmentTrackerDashboardPage;
